import { Injectable } from '@angular/core';
import { Worksheet } from '../models/worksheet';
import { RouteService } from './route.service';
import { RestService } from './rest.service';
import { environment } from '../../environments/environment';
import { HomeworkLink } from '../models/homework-link';
import { WorksheetFactory } from '../models/factories/worksheet-factory';
import { QuickLink, QuickLinkType } from '../models/quick-link';
import { ApplicationStateService } from './application-state.service';
import { QuickLinkService } from './quick-link.service';
import { ModalService } from './modal.service';

@Injectable({
    providedIn: 'root'
})
export class WorksheetService {
    constructor(private applicationStateService: ApplicationStateService,
                private modalService: ModalService,
                private quicklinkService: QuickLinkService,
                private routeService: RouteService,
                private restService: RestService) {}

    /**
     * Get the user's worksheets
     *
     * @returns {Promise<Array<Worksheet>>}
     */
    getWorksheets(): Promise<Array<Worksheet>> {
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.worksheetGet, []);

        return new Promise<Array<Worksheet>>((resolve, reject) => {
            return this.restService.get(route)
            .then(result => {
                const worksheets = [];

                const worksheetFactory = new WorksheetFactory();

                Object.values(result).map((ws) => {
                    worksheets.push(worksheetFactory.buildFromObject(ws));
                });

                resolve(worksheets);
            }).catch(error => {
                reject(error);
            });
        });
    }

    /**
     * Removes a worksheet from the backend
     *
     * @param worksheet
     * @returns {Promise<boolean>}
     */
    deleteWorksheet(worksheet: Worksheet): Promise<boolean> {
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.worksheetDelete, {
            id: worksheet.id
        });

        return new Promise<boolean>((resolve, reject) => {
            return this.restService.post(route, {
                id: worksheet.id
            }).then(result => {
                resolve(result.success)
            }).catch(error => {
                reject(error);
            });
        })
    }

    /**
     * Create new worksheet and save in backend
     *
     * @param homework
     * @returns {Promise<Worksheet>}
     */
    createWorksheet(homework: HomeworkLink|null = null): Promise<Worksheet> {
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.worksheetCreate, []);

        // Set 'Arbeitsblatt' as default name for worksheet
        const request = {
            name: 'Arbeitsblatt',
            homework_id: null
        };

        if (homework?.id) {
            request.homework_id = homework.id;
        }

        return new Promise<Worksheet>((resolve, reject) => {
            return this.restService.post(route, request)
            .then(result => {
                const worksheetFactory = new WorksheetFactory();

                resolve(worksheetFactory.buildFromObject(result));
            }).catch(error => {
                reject(error);
            });
        });
    }

    /**
     * Edit a worksheet
     *
     * @param worksheet
     * @returns {Promise<Worksheet>}
     */
    editWorksheet(worksheet: Worksheet): Promise<Worksheet> {
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.worksheetEdit, []);

        const request = {
            name: worksheet.name,
            id: worksheet.id
        };

        return new Promise<Worksheet>((resolve, reject) => {
            return this.restService.post(route, request)
            .then(result => {
                resolve(result);
            }).catch(error => {
                reject(error);
            });
        });
    }

    /**
     * Open a worksheet
     *
     * @param worksheet
     */
    openWorksheet(worksheet: Worksheet): void {
        const quicklink = new QuickLink();

        quicklink.isSignable = true;
        quicklink.link = worksheet.homework.getLink();
        quicklink.bypassCheck =
            this.applicationStateService.bookConfig.bookPermissions.calcuflowLinks.use;
        quicklink.type = QuickLinkType.CALCUFLOW;
        quicklink.custom_worksheet_id = worksheet.id;
        quicklink.custom_assignments = worksheet.homework.exercises.join(',');
        quicklink.custom_worksheet_updated_at = worksheet.homework.updated;
        quicklink.custom_worksheet_title = worksheet.homework.getTitle();

        this.modalService.closeModal(HomeworkLink.MODAL_ID);
        this.quicklinkService.openQuicklink(quicklink);
    }

    openHomeworkWorksheet(homework: HomeworkLink): void {
        const quicklink = new QuickLink();

        quicklink.isSignable = true;
        quicklink.link = homework.getLink();
        quicklink.bypassCheck =
            this.applicationStateService.bookConfig.bookPermissions.calcuflowLinks.use;
        quicklink.type = QuickLinkType.CALCUFLOW;
        quicklink.custom_worksheet_id = homework.worksheet.id;
        quicklink.custom_assignments = homework.exercises.join(',');
        quicklink.custom_worksheet_updated_at = homework.updated;
        quicklink.custom_worksheet_title = homework.title;

        this.modalService.closeModal(HomeworkLink.MODAL_ID);
        this.quicklinkService.openQuicklink(quicklink);
    }
}
